import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConnectionStatus from '../components/ConnectionStatus';
import Footer from '../components/Footer/Footer';
import AuthService from '../services/authService';
import OrderService from '../services/orderService';
import { Order } from '../types';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import moment from 'moment';
import { formatUTCToLocal, isToday, getLocalMoment } from '../utils/timezoneUtils';

// Đăng ký ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Định nghĩa kiểu sản phẩm
interface Product {
  name: string;
  quantity: number;
}

// Định nghĩa kiểu dữ liệu doanh thu theo ngày
interface DailyRevenue {
  date: string;
  revenue: number;
}

// Giả định thêm dịch vụ thống kê
// import StatisticsService from '../services/statisticsService';

const Home: React.FC = () => {
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    // Thêm dữ liệu cho biểu đồ
    monthlyRevenue: Array(12).fill(0), // Khởi tạo mảng 12 tháng với giá trị 0
    dailyRevenue: [] as DailyRevenue[], // Doanh thu theo ngày
    ordersByStatus: { completed: 0, pending: 0, cancelled: 0 },
    topSellingProducts: [] as Product[],
    customerGrowth: [0, 0, 0, 0, 0, 0],
    // Thêm thống kê cho ngày hiện tại
    todayPendingOrders: 0,
    todayTotalOrders: 0,
    todayRevenue: 0,
    // Thêm thống kê phân khúc giá trị
    priceSegments: {
      under350k: 0,
      from350kTo500k: 0,
      from500kTo700k: 0,
      from700kTo1M: 0,
      over1M: 0
    }
  });

  const isAdmin = AuthService.isAdmin();

  useEffect(() => {
    // Tải dữ liệu đơn hàng từ OrderService
    const loadOrdersData = async () => {
      try {
        const response = await OrderService.getAll();
        const allOrders = response.data;

        // Lọc các đơn hàng của ngày hôm nay
        const today = moment().startOf('day');

        const todayOrders = allOrders.filter((order: Order) => {
          return isToday(order.deliveryDate);
        });

        // Đơn hàng chờ xử lý hôm nay
        const pendingTodayOrders = todayOrders.filter((order: Order) =>
          order.status === 'pending'
        );

        // Tính doanh thu hôm nay, loại bỏ đơn hàng đã hủy
        const todayRevenueValue = todayOrders.reduce((total: number, order: Order) => {
          // Chỉ tính doanh thu cho đơn hàng không phải trạng thái "cancelled"
          if (order.status !== 'cancelled') {
            const orderTotal = (order.price || 0) * (order.quantity || 0);
            return total + orderTotal;
          }
          return total;
        }, 0);

        // Tính tổng doanh thu (không tính đơn hàng đã hủy)
        const totalRevenueValue = allOrders.reduce((total: number, order: Order) => {
          if (order.status !== 'cancelled') {
            const orderTotal = (order.price || 0) * (order.quantity || 0);
            return total + orderTotal;
          }
          return total;
        }, 0);

        // Tính doanh thu theo tháng (không tính đơn hàng đã hủy)
        const currentYear = moment().year();
        const monthlyRevArray = Array(12).fill(0);

        // Tính doanh thu theo ngày (trong tháng hiện tại)
        const dailyRevenueMap = new Map<string, number>();

        // Lấy ngày đầu tiên của tháng hiện tại
        const firstDayOfMonth = moment().startOf('month');

        // Lấy ngày cuối cùng của tháng hiện tại
        const lastDayOfMonth = moment().endOf('month');

        // Tính số ngày trong tháng hiện tại
        const daysInMonth = lastDayOfMonth.date();

        // Khởi tạo dữ liệu cho tất cả các ngày trong tháng
        for (let i = 1; i <= daysInMonth; i++) {
          const date = moment().date(i).startOf('day');
          const dateString = date.format('YYYY-MM-DD');
          dailyRevenueMap.set(dateString, 0);
        }

        // Lọc đơn hàng trong tháng hiện tại
        const currentMonthOrders = allOrders.filter((order: Order) => {
          const orderDate = getLocalMoment(order.deliveryDate);
          return orderDate.isBetween(moment(firstDayOfMonth), moment(lastDayOfMonth), null, '[]') && order.status !== 'cancelled';
        });

        // Tính doanh thu cho từng ngày trong tháng
        currentMonthOrders.forEach((order: Order) => {
          const orderDate = getLocalMoment(order.deliveryDate).startOf('day');
          const dateString = orderDate.format('YYYY-MM-DD');
          const orderTotal = (order.price || 0) * (order.quantity || 0);

          // Cập nhật doanh thu ngày
          dailyRevenueMap.set(dateString, (dailyRevenueMap.get(dateString) || 0) + orderTotal);
        });

        // Tính doanh thu theo tháng cho cả năm
        allOrders.forEach((order: Order) => {
          if (order.status !== 'cancelled') {
            const orderDate = getLocalMoment(order.deliveryDate);
            // Chỉ tính doanh thu trong năm hiện tại
            if (orderDate.year() === currentYear) {
              const month = orderDate.month(); // 0-11
              monthlyRevArray[month] += (order.price || 0) * (order.quantity || 0);
            }
          }
        });

        // Chuyển đổi Map thành mảng để truyền vào state
        // Đảm bảo rằng các ngày được sắp xếp đúng thứ tự
        const dailyRevenueArray: DailyRevenue[] = Array.from(dailyRevenueMap.entries())
          .map(([date, revenue]) => ({ date, revenue }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // Khởi tạo biến đếm cho các phân khúc giá trị
        const priceSegments = {
          under350k: 0,
          from350kTo500k: 0,
          from500kTo700k: 0,
          from700kTo1M: 0,
          over1M: 0
        };

        // Tính toán phân khúc giá trị
        allOrders.forEach((order: Order) => {
          if (order.status !== 'cancelled') {
            const totalValue = (order.price || 0) * (order.quantity || 0);
            if (totalValue < 350000) {
              priceSegments.under350k++;
            } else if (totalValue < 500000) {
              priceSegments.from350kTo500k++;
            } else if (totalValue < 700000) {
              priceSegments.from500kTo700k++;
            } else if (totalValue < 1000000) {
              priceSegments.from700kTo1M++;
            } else {
              priceSegments.over1M++;
            }
          }
        });

        // Cập nhật thống kê
        setStats(prevStats => ({
          ...prevStats,
          totalCustomers: 265,
          totalOrders: allOrders.length,
          totalRevenue: totalRevenueValue,
          pendingOrders: allOrders.filter((order: Order) => order.status === 'pending').length,
          todayPendingOrders: pendingTodayOrders.length,
          todayTotalOrders: todayOrders.length,
          todayRevenue: todayRevenueValue,
          monthlyRevenue: monthlyRevArray,
          dailyRevenue: dailyRevenueArray,
          ordersByStatus: {
            completed: allOrders.filter((order: Order) => order.status === 'completed').length,
            pending: allOrders.filter((order: Order) => order.status === 'pending').length,
            cancelled: allOrders.filter((order: Order) => order.status === 'cancelled').length
          },
          topSellingProducts: [
            { name: 'Sầu riêng', quantity: 258 },
            { name: 'Bưởi da xanh', quantity: 187 },
            { name: 'Măng cụt', quantity: 156 },
            { name: 'Xoài cát', quantity: 132 },
            { name: 'Chôm chôm', quantity: 121 },
          ] as Product[],
          customerGrowth: [35, 43, 28, 52, 48, 59],
          priceSegments: priceSegments
        }));

      } catch (error) {
        console.error('Không thể tải dữ liệu đơn hàng:', error);
      }
    };

    loadOrdersData();
  }, []);

  // Định dạng ngày từ YYYY-MM-DD thành DD/MM
  const formatDateLabel = (dateString: string) => {
    const parts = dateString.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  // Định dạng tên tháng hiện tại
  const getCurrentMonthName = () => {
    const monthNames = [
      'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
      'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
    ];
    return monthNames[new Date().getMonth()];
  };

  // Chart options và data
  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `Doanh thu ${getCurrentMonthName()}`,
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#252422',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('vi-VN', {
                style: 'currency',
                currency: 'VND',
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          },
          title: function (context: any) {
            const currentDate = new Date();
            const month = currentDate.getMonth() + 1; // getMonth() returns 0-11
            return 'Ngày ' + context[0].label + '/' + month;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        titleFont: {
          size: 13
        },
        bodyFont: {
          size: 13
        },
        padding: 10,
        usePointStyle: true
      }
    },
    scales: {
      y: {
        ticks: {
          callback: function (value: any) {
            return value.toLocaleString('vi-VN') + ' đ';
          },
          font: {
            size: 11
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          callback: function (value: any, index: number) {
            // Hiển thị ngày dưới dạng "DD"
            return index + 1;
          },
          font: {
            size: 11
          }
        },
        title: {
          display: true,
          text: `Ngày trong ${getCurrentMonthName()}`,
          font: {
            size: 13
          },
          color: '#252422'
        },
        grid: {
          display: false
        }
      }
    },
    animation: {
      duration: 2000,
      easing: 'easeOutQuart' as const
    },
    elements: {
      bar: {
        borderRadius: 5
      }
    }
  };

  const revenueChartData = {
    labels: stats.dailyRevenue.map(item => {
      const date = new Date(item.date);
      // Đặt giờ về 0 để tránh vấn đề múi giờ
      date.setHours(0, 0, 0, 0);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }),
    datasets: [
      {
        label: 'Doanh thu (VNĐ)',
        data: stats.dailyRevenue.map(item => item.revenue),
        backgroundColor: 'rgba(116, 196, 223, 0.6)', // --bs-primary với opacity
        borderColor: 'rgba(116, 196, 223, 1)', // --bs-primary
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(116, 196, 223, 0.8)',
        barPercentage: 0.7,
        categoryPercentage: 0.7
      },
    ],
  };

  const orderStatusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} đơn (${percentage}%)`;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10
      }
    },
    cutout: '65%',
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 2000
    }
  };

  const orderStatusChartData = {
    labels: ['Hoàn thành', 'Đang xử lý', 'Đã hủy'],
    datasets: [
      {
        data: [stats.ordersByStatus.completed, stats.ordersByStatus.pending, stats.ordersByStatus.cancelled],
        backgroundColor: [
          'rgba(137, 214, 164, 0.8)', // --bs-success
          'rgba(248, 206, 106, 0.8)', // --bs-warning
          'rgba(234, 145, 106, 0.8)', // --bs-danger
        ],
        borderColor: [
          'rgba(137, 214, 164, 1)', // --bs-success
          'rgba(248, 206, 106, 1)', // --bs-warning
          'rgba(234, 145, 106, 1)', // --bs-danger
        ],
        borderWidth: 1,
        hoverOffset: 10
      },
    ],
  };

  const productChartData = {
    labels: stats.topSellingProducts.map(p => p.name),
    datasets: [
      {
        label: 'Số lượng bán',
        data: stats.topSellingProducts.map(p => p.quantity),
        backgroundColor: [
          'rgba(234, 145, 106, 0.8)', // --bs-danger
          'rgba(116, 196, 223, 0.8)', // --bs-primary
          'rgba(248, 206, 106, 0.8)', // --bs-warning
          'rgba(117, 209, 213, 0.8)', // --bs-info
          'rgba(137, 214, 164, 0.8)', // --bs-success
        ],
        borderWidth: 1,
        borderColor: [
          'rgba(234, 145, 106, 1)', // --bs-danger
          'rgba(116, 196, 223, 1)', // --bs-primary
          'rgba(248, 206, 106, 1)', // --bs-warning
          'rgba(117, 209, 213, 1)', // --bs-info
          'rgba(137, 214, 164, 1)', // --bs-success
        ],
        hoverOffset: 10
      },
    ],
  };

  const productChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value} sản phẩm`;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 2000
    }
  };

  const customerGrowthData = {
    labels: ['6 tháng trước', '5 tháng trước', '4 tháng trước', '3 tháng trước', '2 tháng trước', 'Tháng trước'],
    datasets: [
      {
        label: 'Khách hàng mới',
        data: stats.customerGrowth,
        fill: true,
        borderColor: 'rgba(117, 209, 213, 1)', // --bs-info
        backgroundColor: 'rgba(117, 209, 213, 0.2)', // --bs-info với opacity
        tension: 0.4,
        pointBackgroundColor: 'rgba(117, 209, 213, 1)', // --bs-info
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointHoverBackgroundColor: 'rgba(117, 209, 213, 1)', // --bs-info
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const customerGrowthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10,
        titleFont: {
          size: 13
        },
        bodyFont: {
          size: 13
        },
        usePointStyle: true
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    },
    elements: {
      line: {
        borderWidth: 3
      },
      point: {
        hoverRadius: 7
      }
    },
    animation: {
      duration: 2000
    }
  };

  const priceSegmentsChartData = {
    labels: [
      'Dưới 350.000đ ' + '(' + stats.priceSegments.under350k + ' đơn)',
      '350.000đ - 500.000đ ' + '(' + stats.priceSegments.from350kTo500k + ' đơn)',
      '500.000đ - 700.000đ ' + '(' + stats.priceSegments.from500kTo700k + ' đơn)  ',
      '700.000đ - 1.000.000đ ' + '(' + stats.priceSegments.from700kTo1M + ' đơn)',
      'Trên 1.000.000đ ' + '(' + stats.priceSegments.over1M + ' đơn)'
    ],
    datasets: [
      {
        label: 'Số lượng đơn hàng',
        data: [
          stats.priceSegments.under350k,
          stats.priceSegments.from350kTo500k,
          stats.priceSegments.from500kTo700k,
          stats.priceSegments.from700kTo1M,
          stats.priceSegments.over1M
        ],
        backgroundColor: [
          'rgba(117, 209, 213, 0.8)', // --bs-info
          'rgba(116, 196, 223, 0.8)', // --bs-primary
          'rgba(248, 206, 106, 0.8)', // --bs-warning
          'rgba(234, 145, 106, 0.8)', // --bs-danger
          'rgba(137, 214, 164, 0.8)'  // --bs-success
        ],
        borderColor: [
          'rgba(117, 209, 213, 1)', // --bs-info
          'rgba(116, 196, 223, 1)', // --bs-primary
          'rgba(248, 206, 106, 1)', // --bs-warning
          'rgba(234, 145, 106, 1)', // --bs-danger
          'rgba(137, 214, 164, 1)'  // --bs-success
        ],
        borderWidth: 1,
        hoverOffset: 10
      }
    ]
  };

  const priceSegmentsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          padding: 20,
          font: {
            size: 12
          },
          boxWidth: 15,
          boxHeight: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} đơn (${percentage}%)`;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 2000
    }
  };

  return (
    <div className="content">
      {/* Statistics Cards */}
      <div className="row g-4 mb-4">
        <div className="col-lg-4 col-md-6 mb-4">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-body text-center">
              <h6 className="card-title text-muted mb-2 mt-3" style={{ fontSize: '1.5rem' }}>Tổng đơn hôm nay</h6>
              <h3 className="text-success fw-bold mb-0" style={{ fontSize: '2rem' }}>{stats.todayTotalOrders.toLocaleString('vi-VN')}</h3>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-6 mb-4">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-body text-center">
              <h6 className="card-title text-muted mb-2 mt-3" style={{ fontSize: '1.5rem' }}>Đơn cần xử lý hôm nay</h6>
              <h3 className="text-success fw-bold mb-0" style={{ fontSize: '2rem' }}>{stats.todayPendingOrders.toLocaleString('vi-VN')}</h3>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-6 mb-4">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-body text-center">
              <h6 className="card-title text-muted mb-1 mt-3" style={{ fontSize: '1.5rem' }}>Doanh thu hôm nay</h6>
              <small className="text-muted" style={{ fontSize: '0.875rem' }}>
                <i className="far fa-calendar-alt me-1 mb-2"></i> {new Date().toLocaleDateString('vi-VN')}
              </small>
              <h3 className="text-success fw-bold mb-0" style={{ fontSize: '2rem' }}>{stats.todayRevenue.toLocaleString('vi-VN')} đ</h3>

            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="g-4 mb-4">
        <div className="col-lg-8">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-primary text-white" style={{ borderRadius: '10px 10px 0 0' }}>
              <h5 className="card-title" style={{ fontSize: '1.25rem' }}>
                <i className="fas fa-chart-bar me-1"></i>Thống kê doanh thu
              </h5>
            </div>
            <div className="card-body mt-2">
              <Bar data={revenueChartData} options={revenueChartOptions} height={100} />
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-4 mt-4">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-warning text-dark" style={{ borderRadius: '10px 10px 0 0' }}>
              <h5 className="card-title mb-1" style={{ fontSize: '1.25rem' }}>
                <i className="fas fa-chart-pie me-1"></i>Trạng thái đơn hàng
              </h5>
              <p className="card-text small mb-2" style={{ fontSize: '0.875rem' }}>Tỷ lệ đơn hàng theo trạng thái</p>
            </div>
            <div className="card-body mt-4">
              <div style={{ height: '250px', display: 'flex', justifyContent: 'center' }}>
                <Doughnut data={orderStatusChartData} options={orderStatusChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center" style={{ borderRadius: '10px 10px 0 0' }}>
              <div>
                <h5 className="card-title mb-1" style={{ fontSize: '1.25rem' }}>
                  <i className="fas fa-tags me-1"></i>Phân khúc giá trị đơn hàng
                </h5>
                <p className="card-text small mb-2" style={{ fontSize: '0.875rem' }}>Thống kê đơn hàng theo giá trị</p>
              </div>
            </div>

            <div>
              <span className="badge bg-light text-dark mt-2" style={{ fontSize: '0.75rem' }}>
                <i className="fas fa-shopping-cart me-1"></i> Tổng: {Object.values(stats.priceSegments).reduce((a, b) => a + b, 0)} đơn
              </span>
            </div>

            <div className="card-body">
              <div style={{ height: '300px', position: 'relative' }}>
                <Pie data={priceSegmentsChartData} options={priceSegmentsChartOptions} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6 mb-4 mt-4">
          <div className="card h-100 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-info text-white" style={{ borderRadius: '10px 10px 0 0' }}>
              <h5 className="card-title mb-1" style={{ fontSize: '1.25rem' }}>
                <i className="fas fa-users me-1"></i>Khách hàng mới
              </h5>
              <p className="card-text small mb-2" style={{ fontSize: '0.875rem' }}>Tăng trưởng khách hàng 6 tháng gần đây</p>
            </div>
            <div className="card-body mt-4">
              <div style={{ height: '300px' }}>
                <Line data={customerGrowthData} options={customerGrowthOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer chỉ hiển thị ở trang chủ */}
      <Footer />
    </div>
  );
};

export default Home; 